import { useQueryClient } from "@tanstack/react-query";
import type { ExpedienteAnalizado } from "@/types";

type PlaceholderDesc = {
  label: string;
  descripcion: string;
  accion: string;
  por_que?: string;
};

const PLACEHOLDER_DESC: Record<string, PlaceholderDesc> = {
  lectura_aportada: {
    label: "Lectura aportada por el cliente",
    descripcion: "Cifra del medidor que el suscriptor reporta",
    accion: "📞 Pregunta al cliente",
    por_que: "Solo el suscriptor conoce este dato; debe aportarlo por chat, correo o llamada.",
  },
  fecha_lectura_aportada: {
    label: "Fecha de la lectura aportada",
    descripcion: "Día en que el cliente tomó la lectura",
    accion: "📞 Pregunta al cliente",
    por_que: "Acompaña a la lectura aportada; depende del momento en que el suscriptor mira el medidor.",
  },
  correo_electronico: {
    label: "Correo electrónico",
    descripcion: "Para notificar la respuesta",
    accion: "📞 Pregunta al cliente",
    por_que: "El CRM no trae correo confirmado para notificar electrónicamente al suscriptor.",
  },
  lectura_anterior: {
    label: "Lectura anterior del medidor",
    descripcion: "Último registro del periodo previo (kWh)",
    accion: "🔍 SISCOM / portal técnico Afinia",
    por_que:
      "GEMA intentó derivarla matemáticamente (lectura actual − consumo del periodo) pero los valores del WS no son consistentes. Confirma el registro en SISCOM.",
  },
  lectura_actual: {
    label: "Lectura actual del medidor",
    descripcion: "Registro al cierre del periodo facturado",
    accion: "🔍 SISCOM",
    por_que: "El WS de Afinia no aportó la lectura al cierre del ciclo facturado.",
  },
  lectura_ciclo: {
    label: "Lectura del ciclo de facturación",
    descripcion: "Cifra del equipo en la lectura del ciclo",
    accion: "🔍 SISCOM / Mercurio",
    por_que: "Se requiere para contrastar con la lectura aportada; el WS no la entregó.",
  },
  fecha_lectura_ciclo: {
    label: "Fecha de la lectura del ciclo",
    descripcion: "Día en que el técnico tomó la lectura",
    accion: "🔍 SISCOM",
    por_que: "Necesaria para calcular la diferencia de días con la lectura aportada.",
  },
  diferencia_lecturas: {
    label: "Diferencia de lecturas",
    descripcion: "kWh consumidos = actual − anterior",
    accion: "🔍 SISCOM",
    por_que: "Depende de lectura actual y anterior; al faltar una de las dos, GEMA no puede derivarla.",
  },
  periodo_desde: {
    label: "Inicio del periodo facturado",
    descripcion: "Primer día del ciclo",
    accion: "🔍 SISCOM",
    por_que: "El WS no aportó la fecha de corte inicial del ciclo facturado en disputa.",
  },
  periodo_hasta: {
    label: "Fin del periodo facturado",
    descripcion: "Último día del ciclo",
    accion: "🔍 SISCOM",
    por_que: "El WS no aportó la fecha de corte final del ciclo facturado en disputa.",
  },
  dias_del_periodo: {
    label: "Días del periodo",
    descripcion: "Cantidad de días cubiertos por la factura",
    accion: "🔍 SISCOM",
    por_que: "Depende de periodo_desde y periodo_hasta; al faltar una de las dos, GEMA no puede calcularla.",
  },
  nro_medidor: {
    label: "Número del medidor",
    descripcion: "Serial del equipo de medida",
    accion: "🔍 SISCOM / WS Afinia",
    por_que: "No vino en la respuesta del WS de Afinia para esta cuenta-contrato.",
  },
  valor_kwh_mes: {
    label: "Valor del kWh del mes",
    descripcion: "CU en COP/kWh para el periodo",
    accion: "📊 Catálogo de tarifas (auto)",
    por_que: "El catálogo de tarifas local no tiene cargada la resolución vigente para este periodo.",
  },
  total_valor: {
    label: "Total facturado del histórico",
    descripcion: "Suma de los 5 meses",
    accion: "📊 Catálogo de tarifas",
    por_que: "Depende de los valores mensuales; falta al menos un mes para totalizar.",
  },
  promedio_valor: {
    label: "Promedio del histórico",
    descripcion: "total / 5 meses",
    accion: "📊 Catálogo de tarifas",
    por_que: "Depende del total facturado; falta al menos un mes del histórico para promediar.",
  },
  diferencia_dias: {
    label: "Diferencia de días",
    descripcion: "Entre fecha aportada y ciclo",
    accion: "🤖 Calculado automáticamente",
    por_que: "Falta una de las dos fechas (aportada o ciclo) para hacer la resta.",
  },
  consumo_diario: {
    label: "Consumo promedio diario",
    descripcion: "kWh/día = consumo / días",
    accion: "🤖 Calculado automáticamente",
    por_que: "Falta el consumo o los días del periodo para hacer la división.",
  },
  // Desocupación (TIPO_2)
  fecha_desocupacion: {
    label: "Fecha de desocupación",
    descripcion: "Día declarado por el usuario en que dejó de habitar el inmueble",
    accion: "📞 Pregunta al cliente / lee el reclamo",
    por_que: "No se detectó una fecha clara en el motivo del reclamo escrito por el cliente.",
  },
  soporte_desocupacion: {
    label: "Soporte de desocupación",
    descripcion: "Documento que respalda la desocupación (contrato, paz y salvo, declaración)",
    accion: "📞 Solicita al cliente",
    por_que: "Es un documento físico que solo el suscriptor puede aportar; no está en el CRM.",
  },
  // Reiteración (plantillas TIPO_5A/5B)
  numero_decision_anterior: {
    label: "Número de la decisión emitida",
    descripcion: "Consecutivo del oficio de respuesta que AFINIA envió al radicado previo (ej. 202570012345)",
    accion: "📞 Consultar en Mercurio / Documentum",
    por_que: "El antecedente reiterativo fue identificado por GEMA pero el oficio de respuesta no consta en el expediente.",
  },
  fecha_decision_anterior: {
    label: "Fecha de la decisión emitida",
    descripcion: "Día de la firma del oficio de respuesta al radicado previo (formato DD-MM-AAAA)",
    accion: "📞 Consultar en Mercurio / Documentum",
    por_que: "El antecedente reiterativo fue identificado pero el oficio (y su fecha de firma) no está en el expediente.",
  },
  firmante_decision_anterior: {
    label: "Firmante de la decisión previa",
    descripcion: "Nombre del coordinador que firmó la respuesta al radicado previo",
    accion: "📞 Consultar en Mercurio / Documentum",
    por_que: "Sin el oficio físico no es posible saber qué coordinador firmó la respuesta anterior.",
  },
  respuesta_peticiones_usuario: {
    label: "Respuesta a peticiones puntuales",
    descripcion: "Párrafo que contesta las peticiones específicas del usuario (GEMA propone una redacción; el operador puede ajustar)",
    accion: "✍️ Editar texto propuesto",
    por_que: "Bloque editorial: GEMA propone una redacción inicial, pero el operador debe validarla o ajustarla antes de notificar.",
  },
};

function describirPlaceholder(p: string): PlaceholderDesc {
  if (PLACEHOLDER_DESC[p]) return PLACEHOLDER_DESC[p];
  // valor_mes_X / consumo_mes_X / mes_X (slot dinámico)
  if (/^valor_mes_\d$/.test(p))
    return {
      label: `Valor — mes #${p.slice(-1)}`,
      descripcion: "kWh × CU del mes",
      accion: "📊 Catálogo de tarifas",
      por_que: "El WS de Afinia no aportó el consumo o la tarifa de este periodo del histórico.",
    };
  if (/^consumo_mes_\d$/.test(p))
    return {
      label: `Consumo — mes #${p.slice(-1)}`,
      descripcion: "kWh facturados",
      accion: "🔍 WS Afinia (consumo-medido)",
      por_que: "El WS de Afinia no entregó la lectura de consumo para este mes del histórico.",
    };
  if (/^mes_\d$/.test(p))
    return {
      label: `Mes #${p.slice(-1)} del histórico`,
      descripcion: "Etiqueta del periodo",
      accion: "🔍 WS Afinia",
      por_que: "Falta porque depende del consumo de este mes; al no llegar del WS, la etiqueta tampoco se arma.",
    };
  return { label: p, descripcion: "—", accion: "✏️ Validar manualmente" };
}

export function PlaceholdersPendientes({
  expediente,
  onAbrirChat,
}: {
  expediente: ExpedienteAnalizado;
  onAbrirChat: (texto: string) => void;
}) {
  const qc = useQueryClient();
  const aportados = expediente.placeholders_carta ?? {};
  // Solo los REALMENTE pendientes (no incluye los ya aportados).
  const visibles = (expediente.placeholders_pendientes ?? []).filter(
    (p) => !aportados[p],
  );

  // Si no hay pendientes, no mostramos nada. El header de la carta ya
  // muestra "✓ Datos completos" cuando aplica.
  if (visibles.length === 0) return null;

  // Agrupar visibles por categoría según icono de acción.
  const grupos: Record<string, string[]> = {
    cliente: [],
    ws: [],
    catalogo: [],
    derivado: [],
    otro: [],
  };
  for (const p of visibles) {
    const accion = describirPlaceholder(p).accion;
    if (accion.startsWith("📞")) grupos.cliente.push(p);
    else if (accion.startsWith("🔍")) grupos.ws.push(p);
    else if (accion.startsWith("📊")) grupos.catalogo.push(p);
    else if (accion.startsWith("🤖")) grupos.derivado.push(p);
    else grupos.otro.push(p);
  }

  // Helper: del placeholder técnico al texto del prefill que arranca
  // la conversación con GEMA. "fecha_desocupacion" → "Aportar fecha
  // de desocupación: " (luego el operador escribe el valor).
  const prefillDe = (p: string): string => {
    const d = describirPlaceholder(p);
    // Bajamos primera letra para que quede natural tras "Aportar ".
    const lbl =
      d.label.charAt(0).toLowerCase() + d.label.slice(1);
    return `Aportar ${lbl}: `;
  };

  const seccion = (titulo: string, items: string[]) => {
    if (!items.length) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>
          {titulo} · {items.length}
        </div>
        <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {items.map((p) => {
            const d = describirPlaceholder(p);
            return (
              <li key={p}>
                <button
                  type="button"
                  onClick={() => onAbrirChat(prefillDe(p))}
                  title="Pulsa para abrir el chat y completar este dato"
                  style={{
                    width: "100%",
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                    padding: "10px 8px",
                    fontSize: 13,
                    background: "transparent",
                    border: 0,
                    borderBottom: "1px solid var(--border)",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "background .12s",
                    fontFamily: "inherit",
                    color: "inherit",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      "var(--surface-sunken)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      "transparent";
                  }}
                >
                  <span
                    style={{
                      marginTop: 6,
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: "var(--amber)",
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: "var(--ink)" }}>
                      {d.label}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--ink-3)",
                        marginTop: 2,
                      }}
                    >
                      {d.descripcion}
                    </div>
                    {d.por_que && (
                      <div
                        style={{
                          marginTop: 6,
                          padding: "6px 8px",
                          fontSize: 11.5,
                          color: "var(--amber-deep)",
                          background: "var(--amber-soft)",
                          border: "1px solid oklch(0.88 0.07 85)",
                          borderRadius: 4,
                          lineHeight: 1.45,
                        }}
                      >
                        <strong style={{ fontWeight: 600 }}>
                          Por qué GEMA no lo aportó:
                        </strong>{" "}
                        {d.por_que}
                      </div>
                    )}
                  </div>
                  <span
                    className="mono"
                    style={{
                      fontSize: 11,
                      color: "var(--emerald-deep)",
                      background: "var(--emerald-soft)",
                      padding: "2px 7px",
                      borderRadius: 4,
                      border: "1px solid oklch(0.83 0.10 162)",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    💬 Aportar
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div
      className="card"
      style={{
        marginTop: 18,
        borderColor: "oklch(0.88 0.07 85)",
        background: "oklch(0.995 0.018 85)",
      }}
    >
      <div
        className="card__head"
        style={{
          background: "var(--amber-soft)",
          borderBottom: "1px solid oklch(0.88 0.07 85)",
        }}
      >
        <div className="icon" style={{ background: "white" }}>
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M2 3h10v10H2z" />
            <path d="M5 6h4M5 8h4M5 10h3" strokeLinecap="round" />
          </svg>
        </div>
        <h3>
          Datos pendientes para la carta
        </h3>
        <span
          className="meta mono"
          style={{ color: "var(--amber-deep)", fontWeight: 500 }}
        >
          {visibles.length} pendiente{visibles.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="card__body">
        <p
          style={{
            margin: "0 0 16px",
            fontSize: 13,
            color: "var(--ink-2)",
            lineHeight: 1.5,
          }}
        >
          Estos campos no están en el WS de Afinia. Complétalos abajo o pídelos
          al cliente por chat para que la carta descargada quede lista para
          notificar.
        </p>
        {seccion("Aporta el cliente", grupos.cliente)}
        {seccion("Buscar en sistemas técnicos", grupos.ws)}
        {seccion("Catálogo de tarifas (auto)", grupos.catalogo)}
        {seccion("Cálculos automáticos", grupos.derivado)}
        {seccion("Manual", grupos.otro)}

        <div
          style={{
            marginTop: 18,
            padding: "12px 14px",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--r-md)",
            fontSize: 13,
            color: "var(--ink-2)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 18 }}>💬</span>
          <div style={{ flex: 1 }}>
            <strong style={{ color: "var(--ink)" }}>
              Aporta por chat con GEMA
            </strong>
            <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
              Pulsa cualquier fila de arriba o abre el chat con GEMA para
              que el motor detecte el dato y lo agregue al expediente.
            </div>
          </div>
          <button
            type="button"
            onClick={() => onAbrirChat("")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "0 14px",
              height: 32,
              background: "var(--ink)",
              color: "white",
              border: 0,
              borderRadius: "var(--r-sm)",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: 5,
                background:
                  "linear-gradient(135deg, var(--emerald), var(--emerald-deep))",
                display: "grid",
                placeItems: "center",
                fontFamily: "var(--font-mono)",
                fontWeight: 600,
                fontSize: 10,
              }}
            >
              G
            </span>
            Abrir chat
          </button>
        </div>
      </div>
      {/* Placeholder oculto: silencia TS sobre el callback de invalidate. */}
      <span style={{ display: "none" }} aria-hidden>
        {String(!!qc)}
      </span>
    </div>
  );
}
